import type { ReadonlyJSONValue } from "@rocicorp/zero";
import { useEffect } from "react";
import {
  parseStoragePath,
  parseUploadPath,
} from "../../shared/volumes/storagePath";
import type { ChatTool, ToolUseProps } from "../components/Chat/ChatTool";
import { ToolStatus } from "../components/Chat/ToolStatus";
import {
  attachmentPath,
  executionContentPath,
  personalVolumePath,
  volumeFilePath,
} from "../lib/apiPaths";

export type ChatOutputFile = {
  key: string;
  url: string;
  thumbnailUrl: string;
  fileName: string;
};

export type ChatOutputView = ChatOutputFile & {
  mediaType: string;
  sizeBytes: number;
};

type Data = {
  respondingAt: number | null;
  onShowOutput: (output: ChatOutputView) => void;
};

function object(value: unknown) {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, ReadonlyJSONValue>)
    : null;
}

function string(value: ReadonlyJSONValue | undefined) {
  return typeof value === "string" ? value : null;
}

function number(value: ReadonlyJSONValue | undefined) {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

function executionOutputFile(
  executionId: string,
  fileName: string
): ChatOutputFile {
  return {
    key: `execution:${executionId}`,
    url: executionContentPath(executionId, "output"),
    thumbnailUrl: executionContentPath(executionId, "output", { body: true }),
    fileName,
  };
}

function storageFile(path: string): ChatOutputFile | null {
  const upload = parseUploadPath(path);
  if (upload) {
    const url = attachmentPath(upload.attachmentId);
    return {
      key: `storage:${path}`,
      url,
      thumbnailUrl: url,
      fileName: path.slice(path.lastIndexOf("/") + 1),
    };
  }
  const parsed = parseStoragePath(path);
  if (!parsed) return null;
  const { volumeName, relativePath } = parsed;
  const url = volumeFilePath(personalVolumePath(volumeName), relativePath);
  return {
    key: `storage:${path}`,
    url,
    thumbnailUrl: url,
    fileName: relativePath.slice(relativePath.lastIndexOf("/") + 1),
  };
}

// Tool inputs and results both carry the fields that identify the file.
export function chatOutputFile(
  toolName: string,
  value: unknown
): ChatOutputFile | null {
  const fields = object(value);
  if (toolName === "showStorageFile") {
    const path = string(fields?.path);
    return path ? storageFile(path) : null;
  }
  if (toolName !== "showExecutionOutput") return null;
  const executionId = string(fields?.executionId);
  const fileName = string(fields?.fileName);
  return executionId && fileName
    ? executionOutputFile(executionId, fileName)
    : null;
}

export function parseChatOutputView(
  toolName: string,
  output: unknown
): ChatOutputView | null {
  const file = chatOutputFile(toolName, output);
  const fields = object(output);
  const mediaType = string(fields?.mediaType);
  const sizeBytes = number(fields?.sizeBytes);
  return file && mediaType && sizeBytes != null
    ? { ...file, mediaType, sizeBytes }
    : null;
}

function ShowOutputHeader({
  toolUse,
  result,
  data,
  createdAt,
  interrupted,
}: ToolUseProps) {
  const fileName = chatOutputFile(toolUse.toolName, toolUse.input)?.fileName;
  const done = !!result && !result.isError;
  const toolData = data as Data | undefined;
  const respondingAt = toolData?.respondingAt ?? null;
  const onShowOutput = toolData?.onShowOutput;
  const view = parseChatOutputView(toolUse.toolName, result?.output);
  const key = view?.key ?? null;
  const url = view?.url ?? null;
  const thumbnailUrl = view?.thumbnailUrl ?? null;
  const outputFileName = view?.fileName ?? null;
  const mediaType = view?.mediaType ?? null;
  const sizeBytes = view?.sizeBytes ?? null;

  useEffect(() => {
    if (
      key &&
      url &&
      thumbnailUrl &&
      outputFileName &&
      mediaType &&
      sizeBytes != null &&
      respondingAt != null &&
      createdAt >= respondingAt
    ) {
      onShowOutput?.({
        key,
        url,
        thumbnailUrl,
        fileName: outputFileName,
        mediaType,
        sizeBytes,
      });
    }
  }, [
    key,
    url,
    thumbnailUrl,
    outputFileName,
    mediaType,
    sizeBytes,
    respondingAt,
    createdAt,
    onShowOutput,
  ]);

  return (
    <ToolStatus
      icon="output"
      text={
        done
          ? fileName
            ? `Showed ${fileName}`
            : "Showed output"
          : fileName
            ? `Opening ${fileName}…`
            : "Opening output…"
      }
      pending={!result && !interrupted}
    />
  );
}

function showOutputTool(name: string, data: Data): ChatTool {
  return {
    name,
    Header: ShowOutputHeader,
    alwaysLoadOutput: true,
    onActivate: (props) => {
      const view = parseChatOutputView(name, props.result?.output);
      if (view) data.onShowOutput(view);
    },
    data,
  };
}

export const showExecutionOutput = (data: Data): ChatTool =>
  showOutputTool("showExecutionOutput", data);

export const showStorageFile = (data: Data): ChatTool =>
  showOutputTool("showStorageFile", data);
